import { useEffect, useMemo, useRef, useState } from "react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { returnsByMonth, type PerfPoint, type YearReturn } from "@/lib/backtest-analytics";
import { useFormatDate } from "@/hooks/useFormatDate";

// Recharts needs real colors for SVG strokes; these match --primary / --danger.
const STRATEGY = "#00BFA6";
const LOSS = "#EF4444";

function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      if (!e) return;
      const width = Math.floor(e.contentRect.width);
      const height = Math.floor(e.contentRect.height);
      setSize((p) => (p.width === width && p.height === height ? p : { width, height }));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

function Legend() {
  const item = (color: string, label: string) => (
    <span className="inline-flex items-center gap-1.5">
      <i className="inline-block h-[3px] w-3 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-[var(--text-muted)]">
      {item(STRATEGY, "Strategy")}
      {item(LOSS, "Drawdown")}
    </div>
  );
}

function EquityChart({ series }: { series: PerfPoint[] }) {
  const { chartDate, dateTime } = useFormatDate();
  const [eqRef, eqSize] = useSize<HTMLDivElement>();
  const [ddRef, ddSize] = useSize<HTMLDivElement>();
  const domain: [number, number] = [series[0]!.t, series[series.length - 1]!.t];
  const tickDate = (v: number) => chartDate(new Date(v).toISOString());
  const syncId = "bt-perf";

  return (
    // On wide screens the equity and drawdown grow with the page, three to one; below that, fixed heights.
    <div className="flex flex-col lg:min-h-0 lg:flex-1">
      <div ref={eqRef} className="h-[220px] w-full lg:h-auto lg:min-h-[220px] lg:flex-[3]">
        {eqSize.width > 0 && (
          <LineChart width={eqSize.width} height={eqSize.height} data={series} syncId={syncId} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--divider)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="t" type="number" scale="time" domain={domain} hide />
            <YAxis
              width={56}
              tickFormatter={(v) => compact.format(Number(v))}
              tick={{ fill: "var(--text-muted)", fontSize: 11 }}
              stroke="var(--divider)"
              domain={["auto", "auto"]}
            />
            <Tooltip
              content={({ active, payload }) => {
                const p = active ? (payload?.[0]?.payload as PerfPoint | undefined) : undefined;
                if (!p) return null;
                return (
                  <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-card)] px-2.5 py-2 text-xs shadow-sm">
                    <div className="text-[var(--text-muted)]">{dateTime(new Date(p.t).toISOString())}</div>
                    <div className="num mt-1" style={{ color: STRATEGY }}>Strategy {money.format(p.equity)}</div>
                    <div className="num" style={{ color: LOSS }}>Drawdown {p.dd.toFixed(1)}%</div>
                  </div>
                );
              }}
            />
            <Line dataKey="equity" stroke={STRATEGY} strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        )}
      </div>
      <div ref={ddRef} className="h-[80px] w-full lg:h-auto lg:min-h-[80px] lg:flex-1">
        {ddSize.width > 0 && (
          <AreaChart width={ddSize.width} height={ddSize.height} data={series} syncId={syncId} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={domain}
              tickFormatter={tickDate}
              minTickGap={56}
              tick={{ fill: "var(--text-muted)", fontSize: 11 }}
              stroke="var(--divider)"
            />
            <YAxis
              width={56}
              dataKey="dd"
              tickFormatter={(v) => `${Math.round(Number(v))}%`}
              tick={{ fill: "var(--text-muted)", fontSize: 11 }}
              stroke="var(--divider)"
              domain={["dataMin", 0]}
            />
            <Tooltip content={() => null} />
            <Area dataKey="dd" type="stepAfter" stroke={LOSS} strokeWidth={1} fill={LOSS} fillOpacity={0.22} isAnimationActive={false} />
          </AreaChart>
        )}
      </div>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

type Cell = { pct: number; partial: boolean } | undefined;

/** One square per month, a row per year and the year's total last; the stronger the color, the bigger the move. */
function MonthGrid({ months, years }: { months: ReturnType<typeof returnsByMonth>; years: YearReturn[] }) {
  const rows = useMemo(() => {
    const byYear = new Map<number, Cell[]>();
    for (const m of months) {
      const row = byYear.get(m.year) ?? Array<Cell>(12).fill(undefined);
      row[m.month] = { pct: m.pct, partial: m.partial };
      byYear.set(m.year, row);
    }
    const total = new Map(years.map((y) => [y.year, y]));
    return [...byYear.keys()].sort((a, b) => a - b).map((year) => ({ year, cells: byYear.get(year)!, total: total.get(year) }));
  }, [months, years]);
  const max = Math.max(1, ...months.map((m) => Math.abs(m.pct)));
  const [ref, { width }] = useSize<HTMLDivElement>();

  const square = (c: Cell, key: string, title: string, strong = false) => {
    if (!c) return <span key={key} aria-hidden="true" className="h-9 rounded-[5px] bg-[var(--bg-hover)] opacity-50" />;
    const up = c.pct >= 0;
    const a = Math.min(1, Math.abs(c.pct) / max);
    const tone = up ? "var(--success)" : "var(--danger)";
    return (
      <span
        key={key}
        title={`${title}: ${c.pct > 0 ? "+" : ""}${c.pct.toFixed(2)}%${c.partial ? " (part of the period)" : ""}`}
        className={`num flex h-9 items-center justify-center rounded-[5px] text-[11px] ${strong ? "font-semibold" : ""} ${c.partial ? "border border-dashed border-[var(--border-hover)]" : ""}`}
        style={{
          background: `color-mix(in srgb, ${tone} ${Math.round(12 + a * 70)}%, transparent)`,
          color: a > 0.55 ? "#FFFFFF" : tone,
        }}
      >
        {c.pct > 0 ? "+" : c.pct < 0 ? "−" : ""}
        {Math.abs(c.pct) >= 10 ? Math.abs(c.pct).toFixed(0) : Math.abs(c.pct).toFixed(1)}
      </span>
    );
  };

  // Twelve months to a row when they fit; on narrow screens the year wraps onto rows of six or four.
  const perRow = !width || width >= 520 ? 12 : width >= 300 ? 6 : 4;
  const chunks = Array.from({ length: 12 / perRow }, (_, i) => i * perRow);
  const label = "text-center text-[10px] text-[var(--text-muted)]";

  return (
    <div ref={ref} className="grid gap-[3px]" style={{ gridTemplateColumns: `40px repeat(${perRow}, minmax(0, 1fr)) 52px` }}>
      {rows.map((r, ri) =>
        chunks.map((from, ci) => {
          const last = ci === chunks.length - 1;
          // Month names over the first row only, unless the year wraps: then over every row.
          const names = ri === 0 || perRow < 12;
          return [
            ...(names
              ? [
                  <span key={`${r.year}-${from}-h`} />,
                  ...MONTHS.slice(from, from + perRow).map((m) => (
                    <span key={`${r.year}-${m}-h`} className={label}>
                      {m}
                    </span>
                  )),
                  <span key={`${r.year}-${from}-yh`} className={`${label} font-medium`}>
                    {last ? "Year" : ""}
                  </span>,
                ]
              : []),
            <span key={`${r.year}-${from}-label`} className="num self-center text-[11px] text-[var(--text-secondary)]">
              {ci === 0 ? r.year : ""}
            </span>,
            ...r.cells.slice(from, from + perRow).map((c, i) => square(c, `${r.year}-${from + i}`, `${MONTHS[from + i]} ${r.year}`)),
            last ? (
              square(r.total ? { pct: r.total.pct, partial: r.total.partial } : undefined, `${r.year}-total`, String(r.year), true)
            ) : (
              <span key={`${r.year}-${from}-gap`} />
            ),
          ];
        })
      )}
    </div>
  );
}

export function PerformancePanel({ series, years }: { series: PerfPoint[]; years: YearReturn[] }) {
  const months = useMemo(() => returnsByMonth(series), [series]);

  if (series.length < 2) {
    return <div className="py-10 text-center text-sm text-[var(--text-muted)]">No closed trades in this range.</div>;
  }

  return (
    // Fills the space under the results: the equity row takes what is left, the months keep their height.
    <div className="grid flex-1 grid-rows-[1fr_auto] gap-4 p-3.5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] xl:grid-rows-[1fr]">
      <section className="flex min-w-0 flex-col">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <b className="text-[13px] text-[var(--text-primary)]">Equity</b>
          <Legend />
        </div>
        <EquityChart series={series} />
      </section>
      <section className="min-w-0">
        <b className="text-[13px] text-[var(--text-primary)]">Returns by month</b>
        <div className="mb-2.5 mt-0.5 text-xs text-[var(--text-muted)]">
          Which months made or lost the money? One square per month, the year's total last.
        </div>
        <MonthGrid months={months} years={years} />
        {months.some((m) => m.partial) && (
          <div className="mt-2 text-[11px] text-[var(--text-muted)]">Dashed: only part of that month is in the range.</div>
        )}
      </section>
    </div>
  );
}
